import { Box } from "@mui/material";
import { TaskAgentStateManager } from "components/hooks/useTaskAgentState";
import TaskAgentContainer from "./TaskAgentContainer";
import {
  TodoSection,
  BookmarkSection,
  FunctionCallSection,
  UserQuestionSection,
  ChatMessageSection,
  ThinkingSection,
  FollowUpInputSection,
} from "./TaskAgentSections";

interface TaskAgentDisplayProps {
  stateManager: TaskAgentStateManager;
  onAnswerQuestions?: (
    answers: Array<{ functionId: string; answer: string }>,
  ) => void;
  onFollowUpQuestion?: (question: string) => void;
}

export default function TaskAgentDisplay({
  stateManager,
  onAnswerQuestions,
  onFollowUpQuestion,
}: TaskAgentDisplayProps) {
  const { state, computed } = stateManager;
  const { isCurrentlyActive, generalFunctionCalls } = computed;

  const hasContent =
    state.chatMessages ||
    state.thinkingMessage ||
    state.todos.length > 0 ||
    state.bookmarks.length > 0 ||
    state.userQuestions.length > 0 ||
    generalFunctionCalls.length > 0;

  if (!hasContent) return null;

  return (
    <Box sx={{ marginTop: "24px", width: "100%" }}>
      <TaskAgentContainer
        title="Task Agent"
        isProcessing={false}
        processingSteps={[]}
        showProcessingAfterContent={false}
      >
        <Box display="flex" flexDirection="column" gap="24px">
          {/* Progress Section - Always at top */}
          <TodoSection todos={state.todos} />

          {/* Bookmarks Section - Show tracked entities */}
          <BookmarkSection bookmarks={state.bookmarks} />

          {/* Thinking Section - Show when active, near top */}
          <ThinkingSection
            thinkingMessage={state.thinkingMessage}
            isActive={state.isActive}
          />

          {/* Function Calls Section - Always show if has calls */}
          <FunctionCallSection calls={generalFunctionCalls} />

          {/* User Questions Section - Always show if has questions */}
          <UserQuestionSection
            questions={state.userQuestions}
            onAnswerQuestions={onAnswerQuestions}
          />

          {/* Chat Messages Section - Always show if has messages */}
          <ChatMessageSection
            messages={state.chatMessages}
            isCurrentlyActive={isCurrentlyActive}
          />

          {/* Follow-up Input Section - Show when chat is complete and not currently active */}
          {state.chatMessages && !isCurrentlyActive && (
            <FollowUpInputSection onFollowUpQuestion={onFollowUpQuestion} />
          )}
        </Box>
      </TaskAgentContainer>
    </Box>
  );
}
